import { Card, CardContent, FormControlLabel, Grid, LinearProgress, Link, Pagination, Slider, Switch, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import DoiAnalysisLink from "./DoiAnalysisLink";
import ArticleTitle from "./ArticleTitle";
import { ApiResultWrapper, Article, Author, AvailableJournal, calculatePageCount } from "./CommonInterfaces";
import AuthorList from "./AuthorList";
import { auth } from "../firebase/firebase";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import JournalPicker from "./JournalPicker";
import { argosApiEntryPoint } from "../GlobalConfig";

const START_YEAR = 2014;
const END_YEAR = 2025;

const marks = [
    {
        value: START_YEAR,
        label: '' + START_YEAR,
    },
    {
        value: 2020,
        label: '2020',
    },
    {
        value: END_YEAR,
        label: '' + END_YEAR,
    }
];

function valuetext(value: number) {
    return `${value}`;
}

interface JournalExplorerProps{
    initialJournalId: string | undefined
}

function JournalExplorer({initialJournalId}: JournalExplorerProps) {

    const [articles, setArticles] = useState<ApiResultWrapper<Article>>()
    const [isLoading, setIsLoading] = useState(false)
    const [includeHighRisk, setIncludeHighRisk] = useState(true)
    const [returnRetractions, setReturnRetractions] = useState(false)
    const [includeMediumRisk, setIncludeMediumRisk] = useState(false)
    const [includeAnyImpact, setIncludeAnyImpact] = useState(false)
    const [page, setPage] = useState(1)

    const [selectedJournalIds, setSelectedJournalIds] = useState<string[]>()
    const [selectedJournalNames, setSelectedJournalNames] = useState<string[]>()

    const resetPage = () => {
        setPage(1);
    };

    var datasetInitialDate = new Date()
    datasetInitialDate.setFullYear(datasetInitialDate.getFullYear() - 3);
    const [yearRange, setYearRange] = useState<number[]>([START_YEAR, END_YEAR]);

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        setYearRange(newValue as number[]);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    function journalQueryFilter(): string {
        if (selectedJournalIds === undefined || selectedJournalIds.length === 0) {
            return ''
        } else {
            return '&journalOpenAlexIds=' + selectedJournalIds.join(',')
        }
    }

    function riskQueryFilter(): string {

        if (returnRetractions === true) {
            return '&isRetracted=true'
        } else {
            if (includeAnyImpact === true) {
                return '&scitilityRiskLowerBound=0.01'
            } else {
                if (includeMediumRisk === true) {
                    return '&scitilityRiskLowerBound=0.5'
                } else {
                    return '&scitilityRiskLowerBound=1.5'
                }
            }
        }
    }

    useEffect(() => {
        async function fillInitialJournal(journalId: string) {
        let authToken = await auth.currentUser?.getIdToken(true)
        fetch(argosApiEntryPoint + '/journal?filter=name:' + encodeURIComponent(journalId), {
            method: 'GET',
            headers: {
              'content-type': 'application/json;charset=UTF-8',
              'Authorization': 'Bearer ' + authToken
            }
          })
            .then((response) => response.json())
            .then((data: AvailableJournal[]) => {
                if(data.length === 1){
                    setSelectedJournalIds([data[0].journalId]) 
                }
            })
        }

        if(initialJournalId !== undefined){
            fillInitialJournal(initialJournalId)
        }

    }, [])


    useEffect(() => {
        async function fetchData() {
            if (selectedJournalIds !== undefined && selectedJournalIds.length > 0) {
                setIsLoading(true)
                var fromPublicationDate = new Date()
                fromPublicationDate.setFullYear(yearRange[0]);
                fromPublicationDate.setDate(1)
                fromPublicationDate.setMonth(1)
                fromPublicationDate.setHours(0)
                fromPublicationDate.setMinutes(0)
                fromPublicationDate.setSeconds(0)
                fromPublicationDate.setMilliseconds(0)

                var toPublicationDate = new Date()
                toPublicationDate.setFullYear(yearRange[1]);
                toPublicationDate.setDate(31)
                toPublicationDate.setMonth(12)
                toPublicationDate.setHours(23)
                toPublicationDate.setMinutes(59)
                toPublicationDate.setSeconds(59)
                toPublicationDate.setMilliseconds(99)

                let authToken = await auth.currentUser?.getIdToken(true)
                fetch(argosApiEntryPoint + '/risk-score/filter'
                    + '?pageNumber=' + page
                    + '&fromPublicationDate=' + fromPublicationDate?.toISOString().replace('Z', '')
                    + '&toPublicationDate=' + toPublicationDate?.toISOString().replace('Z', '')
                    + journalQueryFilter()
                    + riskQueryFilter()
                    , {
                        method: 'GET',
                        headers: {
                            'content-type': 'application/json;charset=UTF-8',
                            'Authorization': 'Bearer ' + authToken
                        }
                    })
                    .then((response) => response.json())
                    .then((data) => {
                        setIsLoading(false)
                        setArticles(data)
                    })
            }
            // TODO : Error handling setError(true)
        }
        fetchData()
    }, [page, yearRange, selectedJournalIds,
        includeMediumRisk, includeAnyImpact, returnRetractions, includeHighRisk]);


    return <Card variant="outlined" id="journal-explorer" style={{ marginBottom: 40 }}>
        <CardContent>
            <Typography gutterBottom variant="h3" component="div">Journal explorer</Typography>
            <Typography variant="caption">
                Please select at least one journal.
            </Typography>
            <Grid container>
                <Grid item xs={12} md={6} padding={2}></Grid>
                <Grid item xs={12} md={6} sx={{ paddingRight: 2 }}>
                    <Slider
                        aria-labelledby="track-inverted-range-slider"
                        getAriaValueText={valuetext}
                        defaultValue={[START_YEAR, END_YEAR]}
                        onChange={handleSliderChange}
                        min={START_YEAR}
                        valueLabelDisplay="auto"
                        max={END_YEAR}
                        marks={marks}
                    />
                </Grid>
            </Grid>
            <JournalPicker setSelectedJournals={value => {
                resetPage()
                setSelectedJournalIds(value)
                if (value === undefined || value.length === 0) {
                    setArticles(undefined)
                    setIncludeAnyImpact(false)
                    setIncludeMediumRisk(false)
                    resetPage()
                }
            }
            } />
            <FormControlLabel required control={<Switch checked={returnRetractions} />} label="Retractions"
                value={returnRetractions}
                onChange={(_, checked) => {
                    resetPage()
                    setReturnRetractions(checked)
                    setIncludeHighRisk(!checked)
                    setIncludeMediumRisk(false)
                    setIncludeAnyImpact(false)
                }}
            />
            <FormControlLabel required control={<Switch checked={includeHighRisk} />} label="Include High Risk"
                value={includeHighRisk}
                onChange={(_, checked) => {
                    resetPage();
                    setIncludeHighRisk(checked)
                }}
                disabled={
                    returnRetractions
                }
            />
            <FormControlLabel required control={<Switch checked={includeMediumRisk} />} label="Include Medium Risk"
                value={includeMediumRisk}
                onChange={(_, checked) => {
                    resetPage();
                    setIncludeMediumRisk(checked)
                }}
                disabled={
                    returnRetractions
                }
            />
            <FormControlLabel required control={<Switch checked={includeAnyImpact} />} label="Include Any Impact"
                value={includeAnyImpact}
                onChange={(_, checked) => {
                    resetPage()
                    setIncludeAnyImpact(checked)
                }}
                disabled={
                    returnRetractions || selectedJournalIds === undefined || selectedJournalIds?.length === 0
                    || selectedJournalIds?.length > 5
                }
            />

            {isLoading &&
                <LinearProgress />}
            {!isLoading && articles?.data.map((element, index) => (
                <Card variant="outlined" key={"at-risk" + index}>
                    <CardContent>
                        <Typography gutterBottom variant="h6" component="div">
                            <ArticleTitle title={element.title} />
                        </Typography>
                        <AuthorList authors={element.authorships} />
                        <DoiAnalysisLink doi={element.doi} newRetractions={[]} showDoiInTitle={false} />
                        <Link href={element.doi} target="_blank">
                            <Typography>{element.doi}</Typography>
                        </Link>
                    </CardContent>
                </Card>
            ))}
            {!isLoading &&
                <Pagination count={calculatePageCount(articles?.metadata)} page={page} onChange={handlePageChange} style={{ marginTop: 10 }} />
            }
        </CardContent>
    </Card>
}

export default JournalExplorer